import { createApi } from '@reduxjs/toolkit/query/react';

import { apiSahStatements } from '~constants/api';

import { Request, rtkBaseQueryWrapper, unwrap } from '~libs/axiosWrapper';

import { Statement } from '~support-at-home/models';

export const typePrefix = 'statements';

export interface StatementsRequest {
  client_ids: number[];
  start_date: string;
  end_date: string;
}

export interface EstimateResponse {
  statements: {
    to_generate: number;
    existing: number;
    invalid: number;
  };
  items: {
    in_range: number;
    carried_over: number;
    invalid: number;
  };
  errors: ErrorItem[];
  warnings: WarningItem[];
}
export interface GenerateResponse {
  statements_generated: number;
  items_updated: number;
  statements: {
    statement_id: string;
    client_id: number;
    items_count: number;
  }[];
}

export interface ErrorItem {
  id: string;
  ref: string | null;
  reason: string;
  details: string;
  client_id?: number;
  client_name?: string;
  service_date?: string;
}

export interface WarningItem {
  id: number;
  client_name: string;
  reason: string;
  details: string;
}

interface StatementsWithCount {
  total: number;
  count: number;
  rows: Statement[];
}

export const statementsApi = createApi({
  reducerPath: 'statementsApi',
  baseQuery: rtkBaseQueryWrapper({ baseUrl: apiSahStatements }),
  tagTypes: ['statements', 'estimates'],
  endpoints: (builder) => ({
    getStatements: builder.query<StatementsWithCount, Request | void>({
      query: (params) => ({ url: '', method: 'GET', params }),
      providesTags: [{ type: 'statements', id: 'LIST' }],
      transformResponse: (payload) => unwrap<StatementsWithCount>(payload),
    }),
    getStatement: builder.query<Statement, Request & { id: string }>({
      query: ({ id, ...rest }) => ({ url: `/${id}`, method: 'GET', params: rest }),
      providesTags: (_result, _error, { id }) => [{ type: 'statements', id }],
      transformResponse: (payload) => unwrap<Statement>(payload),
    }),
    estimate: builder.query<EstimateResponse, StatementsRequest>({
      query: (params) => ({ url: '/estimate', method: 'GET', params }),
      providesTags: [{ type: 'estimates', id: 'LIST' }],
      transformResponse: (payload) => unwrap<EstimateResponse>(payload),
    }),
    generate: builder.mutation<GenerateResponse, StatementsRequest>({
      query: (body) => ({ url: '/generate', method: 'POST', body }),
      invalidatesTags: [
        { type: 'statements', id: 'LIST' },
        { type: 'estimates', id: 'LIST' },
      ],
      transformResponse: (payload) => unwrap<GenerateResponse>(payload),
    }),
  }),
});

export const { useGetStatementsQuery, useGetStatementQuery, useEstimateQuery, useGenerateMutation } = statementsApi;
